import fetch from '../core/fetch-extension';
import config from '../core/config';
import { Err, Result, Ok } from '../core/result';
import { ApiError } from '../core/errors';
import { SuccessResponse, OkResult } from '../core/params';

interface ReservationResult {
    DNIS: string;
}

interface ClientSeenQuery {
    app?: string;
}

export interface SessionData {
    playStream: string;
    playToken: string;
    publishStream?: string;
    publishToken?: string;
    streamTransportType: string;
    wowza: string;
    id: string;
}

export enum SessionType {
    Video       = 'VIDEO',
    VideoCall   = 'VIDEOCALL',
    Peek        = 'PEEK'
}

interface RequestPayload {
    performerId: number;
    clientId: number;
    type: SessionType;
    name: string;
    ivrCode?: string;
    payment?: string;
    streamInfo?: any;
}

interface SessionCancelPayload {
    clientId: number;
    performerId: number;
}

interface PerformerTimeoutPayload {
    clientId: number;
    performerId: number;
}

interface StartCallPayload {
    ivrCode: string;
}

export type VoyeurType = 'VOYEURPEEK' | 'VOYEUR' | 'VOYEURCLIENT' | 'VIDEOSTREAM';
export interface InitiatePayload {
    chatroomName?: string;
    clientId?: number;
    ivrCode?: string;
    performerId?: number;
    type?: VoyeurType;
}

export interface EndPayload {
    clientId?: number;
    performerId?: number;
    type?: VoyeurType;
    blocked?: boolean;
}

export type ReservationResponse = Result<ReservationResult, ApiError>;
export type ClientSeenResponse = Result<SuccessResponse, ApiError>;
export type RequestResponse = Result<OkResult, ApiError>;
export type OkResponse = Result<OkResult, ApiError>;
export type InitiateResponse = Result<SessionData, ApiError>;
export type BroadcastResponse = Result<SessionData, ApiError>;

// NEW
export async function createPhoneReservation(advertId: number): Promise<ReservationResponse>{
    const result = await fetch(`${config.ApiUrl}/session/make_phone_reservation/${advertId}`);

    if(!result.ok){
        return Err(new ApiError(result.status));
    }

    const data: ReservationResult = await result.json();

    return Ok(data);
}

// OLD
export async function createReservation(advertId: number): Promise<ReservationResponse>{
    const result = await fetch(`${config.ApiUrl}/session/make_reservation/${advertId}/PHONE?_format=json`);

    if(!result.ok){
        return Err(new ApiError(result.status));
    }

    const data: ReservationResult = await result.json();

    return Ok(data);
}

export async function clientSeen(query: ClientSeenQuery = {}): Promise<ClientSeenResponse> {
    const result = await fetch(`${config.ApiUrl}/session/client_seen`, {
        query
    });

    if(!result.ok){
        return Err(new ApiError(result.status));
    }

    const data = await result.json();

    return Ok(data);
}

export async function startRequest(payload: RequestPayload): Promise<RequestResponse> {
    const action = payload.type === SessionType.Peek ? 'peek' : 'chat';
    const result = await fetch(`${config.ApiUrl}/session/request/${action}`, {
        method: 'POST',
        body: payload
    });

    const data = await result.json();

    if(!result.ok || (result.ok && data.error)){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function initiate(sessionType: SessionType, advertId: number, payload: InitiatePayload): Promise<InitiateResponse> {

    const isVideoChat = sessionType === SessionType.Video || sessionType === SessionType.Peek;

    const url = isVideoChat ?
        `/performer_account/performer_number/${advertId}/initiate_videochat` :
        `/performer_account/${advertId}/initiate_videocall`;

    const result = await fetch(`${config.ApiUrl}/session${url}`, {
        method: 'POST',
        body: payload
    });

    const data = await result.json();

    if(!result.ok || data.error){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function cancel(payload: SessionCancelPayload): Promise<RequestResponse> {
    const result = await fetch(`${config.ApiUrl}/session/cancel`, {
        method: 'POST',
        body: payload
    });

    const data = await result.json();

    if(!result.ok || data.error){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function end(payload: EndPayload): Promise<RequestResponse> {
    const result = await fetch(`${config.ApiUrl}/session/end`, {
        method: 'POST',
        body: payload
    });

    const data = await result.json();

    if(!result.ok || data.error){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function deleteVideorequest(performerId: number): Promise<RequestResponse>{
    const result = await fetch(`${config.ApiUrl}/session/videochat_request/${performerId}`, {
        method: 'DELETE',
    });

    const data = await result.json();

    if(!result.ok || data.error){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function performerTimeout(payload: PerformerTimeoutPayload): Promise<RequestResponse> {
    const result = await fetch(`${config.ApiUrl}/session/timeout/performer`, {
        method: 'POST',
        body: payload
    });

    const data = await result.json();

    if(!result.ok || data.error){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function startCall(performerId: number, payload: StartCallPayload): Promise<RequestResponse> {
    const result = await fetch(`${config.ApiUrl}/session/start_audio/${performerId}`, {
        method: 'POST',
        body: payload
    });

    const data = await result.json();

    if(!result.ok || data.error){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function endCall(payload: StartCallPayload): Promise<RequestResponse> {
    const result = await fetch(`${config.ApiUrl}/session/stop_audio`, {
        method: 'POST',
        body: payload
    });

    const data = await result.json();

    if(!result.ok || data.error){
        return Err(new ApiError(result.status, data.error));
    }

    return Ok(data);
}

export async function startBroadcasting(performerId: number): Promise<BroadcastResponse> {
    const result = await fetch(`${config.ApiUrl}/performer/performer_accounts/${performerId}/broadcast/video`, {
        method: 'POST'
    });

    if(!result.ok){
        return Err(new ApiError(result.status));
    }

    const data = await result.json();

    return Ok(data);
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./node_modules/sensejs/session/index.ts